<template>
  <page-header title="Data Centers" class="z-10 relative">
    <router-link
      to="/data-center"
      class="btn-primary ml-auto h-10"
    >
      <PlusCircleIcon class="size-5 -ml-1 mr-2" />
      Add
    </router-link>
  </page-header>

  <grid-items-empty v-if="isEmpty && search === null" :link="`/data-center`" />

  <template v-else>
    <geo-map v-if="dataCenters.length" :data-centers="dataCenters" />

    <div class="flex justify-between mb-4 z-10 relative">
      <input-search v-model.trim="search" class="max-w-lg grow" />

      <reload-button class="ml-4" @click="reloadGrid" />
    </div>

    <grid
      ref="gridRef"
      :data-handler="getDataCenters"
      :columns="columns"
      :row-class="rowClass"
      :per-page="100"
      @enable="onEnable"
      @disable="onDisable"
    />
  </template>
</template>

<script setup>
import { ref, markRaw } from 'vue';
import PageHeader from '../components/PageHeader.vue';
import ReloadButton from '../components/grid/ReloadButton.vue';
import InputSearch from '../components/grid/InputSearch.vue';
import GridItemsEmpty from '../components/grid/GridItemsEmpty.vue';
import DataCenterListActions from '../components/DataCenterListActions.vue';
import GeoMap from '../components/GeoMap.vue';
import Grid from '../components/grid/Grid.vue';
import { PlusCircleIcon } from '@heroicons/vue/24/outline';
import { useFetch, watchDebounced } from '@vueuse/core';
import { useRouteQuery } from '@vueuse/router';
import { useRouter } from 'vue-router';
import { apiDomain } from '../composables/useConstants';
import { Modal } from '../composables/useModal';
import { useToast } from 'vue-toastification';
import { formatDate } from '../utils/formatters';

const DISABLED = '🔴';
const ENABLED = '🟢';

const gridRef = ref(null);
const isEmpty = ref(false);
const router = useRouter();
const toast = useToast();
const dataCenters = ref([]);

function rowClass(_row, index) {
  return (index % 2 ? 'bg-zinc-300/10' : '') + (_row.isEnabled ? '' : '[&>td:not(:last-child)]:!opacity-30'); // stripes class for row
}

function reloadGrid(updateQuery = {}) {
  if (gridRef.value?.reload) {
    gridRef.value.reload(updateQuery);
  }
}

const search = useRouteQuery('search', null);

watchDebounced(
  search,
  () => {
    reloadGrid({ offset: 0 });
  },
  { debounce: 500, maxWait: 1000 }
);

function getUrl({ limit, offset, sort, direction }) {
  const data = { limit, offset, order: sort, direction: direction?.toLowerCase(), search: search.value ?? '' };

  const searchParams = new URLSearchParams(data);

  return `${apiDomain}/api/data-center?${searchParams}`;
}

async function getDataCenters(args) {
  const { limit, offset, sort, direction } = args;
  const { data } = await useFetch(getUrl({ limit, offset, sort, direction })).get().json();

  isEmpty.value = !data.value.total;
  dataCenters.value = data.value.dataCenters;

  return {
    total: data.value.total,
    rows: data.value.dataCenters
  };
}

const columns = [
  {
    title: '#',
    prop: 'id',
    class: 'text-xs font-mono cursor-pointer',
    click: row => router.push(`/data-center/${row.id}`),
    sortable: true
  },
  {
    title: 'Name',
    prop: 'name',
    class: 'cursor-pointer',
    click: row => router.push(`/data-center/${row.id}`),
    sortable: true
  },
  {
    title: 'Status',
    value: row => row.isEnabled ? ENABLED : DISABLED,
    class: 'cursor-pointer',
    click: row => router.push(`/data-center/${row.id}`)
  },
  {
    title: 'Country',
    prop: 'country',
    sortable: true,
    class: 'cursor-pointer',
    click: row => router.push(`/data-center/${row.id}`)
  },
  {
    title: 'City',
    prop: 'city',
    class: 'cursor-pointer',
    click: row => router.push(`/data-center/${row.id}`)
  },
  {
    title: 'CreatedAt',
    prop: 'createdAt',
    value: row => formatDate(row.createdAt),
    sortable: true
  },
  {
    class: 'no-animation text-right max-w-max! z-10 relative',
    component: markRaw(DataCenterListActions)
  }
];

async function onEnable(row) {
  const confirm = await Modal.confirm({
    title: 'Enable Data Center',
    body: `Enable Data Center "${row.name}"?`,
    okButton: 'Enable',
    iconColor: 'primary'
  });

  if (!confirm) {
    return;
  }

  const { data } = await useFetch(`${apiDomain}/api/data-center/enable/${row.id}`).get().json();

  if (data.value?.success) {
    toast.success('Data Center enabled successfully');
    reloadGrid();
    return;
  }

  toast.error('Error enabling Data Center');
}

async function onDisable(row) {
  const confirm = await Modal.confirm({
    title: 'Disable Data Center',
    body: `Disable Data Center "${row.name}"?`,
    okButton: 'Disable',
    iconColor: 'primary'
  });

  if (!confirm) {
    return;
  }

  const { data } = await useFetch(`${apiDomain}/api/data-center/disable/${row.id}`).get().json();

  if (data.value?.success) {
    toast.success('Data Center disabled successfully');
    reloadGrid();
    return;
  }

  toast.error('Error disabling Data Center');
}
</script>
